import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Paper, Box, Typography, Chip, Button, CircularProgress, Alert } from '@mui/material';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import { getPlannerMonth } from '../../../../Action/api';
import { getUserDataFromToken } from '../../../../Utils/urlAuthHandler';
import { ACTIVITY_TYPE_COLORS } from '../plannerMockData';
import { ACTIVITY_TYPE_ICONS } from './ActivityBlock';
import { getActivityLabel } from './WeeklyGrid';
import {
  getWeekStart,
  getWeekDates,
  getWeekKey,
  getMonthKey,
  getDayKey,
  WEEKDAY_LABELS,
  DAILY_TARGET_MINUTES,
  formatDuration,
  formatWeekRange,
  calculateSummary,
  minutesToTimeLabel,
} from '../plannerUtils';

const STATUS_COLOR = { Draft: 'default', Submitted: 'info', Approved: 'success', Rejected: 'error' };

const CurrentWeekDashboardCard = () => {
  const navigate = useNavigate();
  const { uId: plannerUserId } = getUserDataFromToken();

  const [state, setState] = useState({ loading: true, error: null, activities: [], meta: { status: 'Draft', approvedBy: null } });

  const weekStart = useMemo(() => getWeekStart(new Date()), []);
  const weekDates = useMemo(() => getWeekDates(weekStart), [weekStart]);
  const weekKey = getWeekKey(weekStart);
  const owningMonth = getMonthKey(weekStart);
  const touchedMonths = useMemo(
    () => Array.from(new Set(weekDates.map((d) => getMonthKey(d)))),
    [weekDates]
  );

  useEffect(() => {
    let cancelled = false;
    setState((s) => ({ ...s, loading: true, error: null }));

    Promise.all(touchedMonths.map((m) => getPlannerMonth(plannerUserId, m).catch(() => null)))
      .then((docs) => {
        if (cancelled) return;
        const activities = [];
        docs.forEach((doc) => {
          if (!doc) return;
          doc.activities.forEach((a) => {
            if (weekDates.some((d) => getDayKey(d) === a.day)) activities.push(a);
          });
        });
        const owningDoc = docs[touchedMonths.indexOf(owningMonth)];
        const meta = owningDoc?.weeks?.[weekKey] || { status: 'Draft', approvedBy: null };
        setState({ loading: false, error: null, activities, meta });
      })
      .catch((err) => {
        if (cancelled) return;
        setState((s) => ({ ...s, loading: false, error: err.message || 'Failed to load this week’s plan.' }));
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plannerUserId, weekKey]);

  const summary = useMemo(() => calculateSummary(state.activities), [state.activities]);
  const todayKey = getDayKey(new Date());

  return (
    <Paper elevation={2} sx={{ p: 3, borderRadius: 3, backgroundColor: '#fff', boxShadow: '0px 4px 20px rgba(0,0,0,0.05)', mb: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 1, mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <CalendarMonthIcon sx={{ color: '#1976d2' }} />
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#333' }}>
              This Week's Plan
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {formatWeekRange(weekStart)}
            </Typography>
          </Box>
          {!state.loading && !state.error && (
            <Chip
              size="small"
              label={state.meta.status}
              color={STATUS_COLOR[state.meta.status] || 'default'}
              sx={{ fontWeight: 600, ml: 0.5 }}
            />
          )}
        </Box>
        <Button
          size="small"
          endIcon={<ArrowForwardIcon />}
          onClick={() => navigate('/planner')}
        >
          View Full Planner
        </Button>
      </Box>

      {state.loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={28} />
        </Box>
      )}

      {!state.loading && state.error && (
        <Alert severity="warning" sx={{ borderRadius: 2 }}>
          {state.error}
        </Alert>
      )}

      {!state.loading && !state.error && state.activities.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            You haven't planned this week yet.
          </Typography>
          <Button variant="outlined" size="small" onClick={() => navigate('/planner')}>
            Plan This Week
          </Button>
        </Box>
      )}

      {!state.loading && !state.error && state.activities.length > 0 && (
        <>
          <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', mb: 2.5 }}>
            <StatItem label="Planned" value={formatDuration(summary.plannedWorkMinutes)} color="#1976d2" />
            <StatItem label="Completed" value={formatDuration(summary.completedWorkMinutes)} color="#2e7d32" />
            <StatItem label="Remaining" value={formatDuration(summary.remainingWorkMinutes)} color="#ed6c02" />
            <StatItem label="Break" value={formatDuration(summary.plannedBreakMinutes)} color="#8d6e63" />
          </Box>

          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(6, minmax(140px, 1fr))', gap: 1.5, overflowX: 'auto' }}>
            {weekDates.map((date, i) => {
              const dayKey = getDayKey(date);
              const dayActivities = state.activities
                .filter((a) => a.day === dayKey)
                .sort((a, b) => a.startMinutes - b.startMinutes);
              const totalMinutes = dayActivities.reduce((sum, a) => sum + (a.endMinutes - a.startMinutes), 0);
              const belowMinimum = totalMinutes < DAILY_TARGET_MINUTES;
              const isToday = dayKey === todayKey;
              const isPastDay = dayKey < todayKey;

              return (
                <Box
                  key={dayKey}
                  sx={{
                    border: isToday ? '2px solid #1976d2' : '1px solid rgba(0,0,0,0.08)',
                    borderRadius: 2,
                    p: 1,
                    minWidth: 0,
                    height: 280,
                    display: 'flex',
                    flexDirection: 'column',
                    backgroundColor: isToday ? '#e3f2fd' : isPastDay ? '#f5f5f5' : '#fff',
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 0.5, flexShrink: 0 }}>
                    <Typography
                      variant="caption"
                      sx={{ fontWeight: 700, color: isToday ? '#1976d2' : isPastDay ? '#9e9e9e' : '#333' }}
                    >
                      {WEEKDAY_LABELS[i].slice(0, 3)}
                      {isToday ? ' · Today' : ''}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ color: isToday ? '#1976d2' : isPastDay ? '#bdbdbd' : 'text.secondary', fontWeight: isToday ? 700 : 400 }}
                    >
                      {date.getDate()}
                    </Typography>
                  </Box>
                  <Typography
                    variant="caption"
                    sx={{ display: 'block', mb: 1, fontWeight: 600, color: belowMinimum ? '#9e9e9e' : '#2e7d32', flexShrink: 0 }}
                  >
                    {formatDuration(totalMinutes)}
                  </Typography>

                  {dayActivities.length === 0 ? (
                    <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                      No activities
                    </Typography>
                  ) : (
                    <Box sx={{ overflowY: 'auto', minHeight: 0 }}>
                      {dayActivities.map((a) => {
                        const color = ACTIVITY_TYPE_COLORS[a.type] || '#546e7a';
                        const TypeIcon = ACTIVITY_TYPE_ICONS[a.type] || MoreHorizIcon;
                        return (
                          <Box
                            key={a.id}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0.5,
                              borderLeft: '3px solid',
                              borderLeftColor: color,
                              borderRadius: 1,
                              px: 0.75,
                              py: 0.5,
                              mb: 0.5,
                              backgroundColor: `${color}0f`,
                            }}
                          >
                            <TypeIcon fontSize="inherit" style={{ color }} />
                            <Box sx={{ minWidth: 0 }}>
                              <Typography variant="caption" noWrap sx={{ display: 'block', fontWeight: 600 }} title={getActivityLabel(a)}>
                                {getActivityLabel(a)}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {minutesToTimeLabel(a.startMinutes)}
                              </Typography>
                            </Box>
                          </Box>
                        );
                      })}
                    </Box>
                  )}
                </Box>
              );
            })}
          </Box>
        </>
      )}
    </Paper>
  );
};

const StatItem = ({ label, value, color }) => (
  <Box>
    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
      {label}
    </Typography>
    <Typography variant="subtitle1" sx={{ fontWeight: 700, color }}>
      {value}
    </Typography>
  </Box>
);

export default CurrentWeekDashboardCard;
